import { createRouter, createWebHistory } from 'vue-router'
import MainLayout from '@/components/templates/MainLayout.vue'
import { useAuthStore } from '@/stores/auth.store'
import type { AccessLevel } from '@/types/auth.types'

// Convenience aliases (extend as you add role-gated routes)
const ALL: AccessLevel[] = ['access_level.admin', 'access_level.manager', 'access_level.user']
const ADMIN_ONLY: AccessLevel[] = ['access_level.admin']
const ADMIN_MANAGER: AccessLevel[] = ['access_level.admin', 'access_level.manager']

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Auth routes (outside MainLayout)
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/auth/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/Dashboard.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/account',
          name: 'account',
          component: () => import('@/views/Account.vue'),
          meta: { access_levels: ALL },
        },

        // Dashboards
        {
          path: '/dashboards',
          name: 'dashboards',
          component: () => import('@/views/dashboards/Dashboards.vue'),
          meta: { access_levels: ALL },
        },

        // AI Dashboards (admin only)
        {
          path: '/ai-dashboards',
          name: 'ai-dashboards',
          component: () => import('@/views/ai-dashboards/AiDashboards.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },

        // Code Quality — coverage & complexity. ALL, like /dashboards: these
        // metrics carry no per-person data, so there is nothing to gate on.
        {
          path: '/code-quality',
          name: 'code-quality',
          component: () => import('@/views/code-quality/CodeQuality.vue'),
          meta: { access_levels: ALL },
        },

        // OAuth 2.0 machine auth — applications (admin only)
        {
          path: '/applications',
          name: 'application-list',
          component: () => import('@/views/application/ApplicationList.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        {
          path: '/applications/new',
          name: 'application-create',
          component: () => import('@/views/application/ApplicationCreate.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        {
          path: '/applications/:id',
          name: 'application-detail',
          component: () => import('@/views/application/ApplicationDetail.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },

        // OAuth 2.0 machine auth — signing keys (admin only)
        {
          path: '/signing-keys',
          name: 'signing-key-list',
          component: () => import('@/views/signingKey/SigningKeyList.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        {
          path: '/signing-keys/new',
          name: 'signing-key-create',
          component: () => import('@/views/signingKey/SigningKeyCreate.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        {
          path: '/signing-keys/:id',
          name: 'signing-key-detail',
          component: () => import('@/views/signingKey/SigningKeyDetail.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },

        // OAuth 2.0 machine auth — tokens (read-only flat list, admin only)
        {
          path: '/tokens',
          name: 'token-list',
          component: () => import('@/views/token/TokenList.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        // Configuration — admin + manager read, admin-only write
        {
          path: '/configuration',
          name: 'configuration-home',
          component: () => import('@/views/configuration/ConfigurationHome.vue'),
          meta: { access_levels: ADMIN_MANAGER },
        },
        {
          path: '/configuration/staff',
          name: 'employee-list',
          component: () => import('@/views/configuration/EmployeeList.vue'),
          meta: { access_levels: ADMIN_MANAGER },
        },
        {
          path: '/configuration/staff/unregistered',
          name: 'employee-unregistered-list',
          component: () => import('@/views/configuration/UnregisteredEmployeeList.vue'),
          meta: { access_levels: ADMIN_MANAGER },
        },
        {
          path: '/configuration/staff/new',
          name: 'employee-create',
          component: () => import('@/views/configuration/EmployeeForm.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        {
          path: '/configuration/staff/:id',
          name: 'employee-detail',
          component: () => import('@/views/configuration/EmployeeForm.vue'),
          meta: { access_levels: ADMIN_MANAGER },
        },
        {
          path: '/configuration/issue-queries',
          name: 'issue-squad-query-list',
          component: () => import('@/views/configuration/IssueQueryList.vue'),
          meta: { access_levels: ADMIN_MANAGER },
        },
        {
          path: '/configuration/issue-queries/new',
          name: 'issue-squad-query-create',
          component: () => import('@/views/configuration/IssueQueryForm.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        {
          path: '/configuration/issue-queries/:id',
          name: 'issue-squad-query-detail',
          component: () => import('@/views/configuration/IssueQueryForm.vue'),
          meta: { access_levels: ADMIN_MANAGER },
        },
        // Add domain-entity routes here — see docs/guides/adding-entities.md
      ],
    },
  ],
})

// Navigation guard — redirect to login if not authenticated, check role access
let authInitialized = false

/** Called from main.ts after auth.init() completes */
export function markAuthInitialized() {
  authInitialized = true
}

router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // On first navigation (page load), auth might not be initialized yet.
  // Wait for it — init() is called in main.ts before mount, but the
  // router may start resolving before mount completes.
  if (!authInitialized) {
    try {
      await authStore.init()
    } catch {
      // init failed — will redirect to login below
    }
    authInitialized = true
  }

  if (!authStore.isAuthenticated) {
    return { name: 'login' }
  }

  // Role-based access check
  const requiredRoles = to.meta.access_levels as AccessLevel[] | undefined
  if (requiredRoles && authStore.currentUser) {
    if (!requiredRoles.includes(authStore.currentUser.access_level)) {
      return { name: 'dashboard' }
    }
  }

  return true
})

export default router
